import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { Option } from "../../models/Option";
import { Header } from "../../components/shared/Header";
import { Select } from "../../components/shared/Select";
import { Input } from "../../components/shared/inputs/Input";
import { QuillEditor } from "../../components/shared/QuillEditor";
import { Button } from "../../components/shared/Button";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { ResponseModel } from "../../models/ResponseModel";
import { getPrivateDiploma, postPrivateDiplomaEdit } from "../../services/DiplomaService";
import { Diploma } from "../../models/Diploma";

export const PrivateDiplomaEditPage: React.FC = () => {   
    let params = useParams(); 
    const diplomaId: number = +params.diplomaId!;    
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("diploma-edit-page"));  
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const [categories, setCategories] = useState<Option[]>([]);
    const [institutes, setInstitutes] = useState<Option[]>([]);
    const [diplomaPeriods, setDiplomaPeriods] = useState<Option[]>([]);
    const [diploma, setDiploma] = useState<Diploma>();
    const [title, setTitle] = useState<string>();
    const [subTitle, setSubTitle] = useState<string>(); 
    const [loading, setLoading] = useState<boolean>(true); 
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getDiploma();
        }
    }, []);
    
    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("diploma-edit-page")); 
    }, [isMobile]);   

    async function getDiploma() {                
        await getPrivateDiploma(diplomaId, admin!.adminId, admin!.token)
        .then((data: any) => {
            setCategories(data.categories);
            setInstitutes(data.institutes?? []);    
            setDiplomaPeriods(data.diplomaPeriods?? []);   
            setDiploma(data.diploma?? []);
            setTitle(data.diploma.privateDiplomaName_SIN);
            setSubTitle(data.diploma.privateDiplomaName_ENG);    
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const handleInstituteChange = (instituteId: number) => {        
        setDiploma((values: any) => ({ ...values, ["privateInstituteId"]: instituteId }));         
    }

    const handleDiplomaPeriodChange = (diplomaPeriodId: number) => {        
        setDiploma((values: any) => ({ ...values, ["privateDiplomaPeriodId"]: diplomaPeriodId }));              
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setDiploma((values: any) => ({ ...values, [name]: value }));
    }

    const handleContentChange = (name: string, content: string) => {        
        setDiploma((values: any) => ({ ...values, [name]: content }));
    }

    async function postEditResponse () {            
        await postPrivateDiplomaEdit(diploma!, admin!.adminId, admin!.token)
        .then((response: ResponseModel) => {                
            setOpenDialog(true);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                               
    }

    const closeDialog = () => {
        setOpenDialog(false);
        navigate('/private-diplomas');        
    }

    return (
        <>
            <Header /> 
            {loading ? <></> : <div style={styles}>
                <div style={styles.titleWrapper}>
                    <h2 style={styles.title}>{title}</h2>
                    <h3 style={styles.title}>{subTitle}</h3>
                </div>                
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Name [SIN]"}</label>
                        <Input
                            type={"text"}
                            name={"privateDiplomaName_SIN"}
                            value={diploma?.privateDiplomaName_SIN}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Name [TAM]"}</label>
                        <Input
                            type={"text"}
                            name={"privateDiplomaName_TAM"}
                            value={diploma?.privateDiplomaName_TAM}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Name [ENG]"}</label>
                        <Input
                            type={"text"}
                            name={"privateDiplomaName_ENG"}
                            value={diploma?.privateDiplomaName_ENG}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Categories"}</label>
                        <Select 
                            styles={styles.searchSelect}
                            label={"Categories"} 
                            options={categories}                             
                            getSelectedValue={() => {}} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Institute"}</label>
                        <Select 
                            styles={styles.searchSelect}
                            label={"Institute"} 
                            options={institutes} 
                            initialValue={diploma?.privateInstituteId!.toString()}
                            getSelectedValue={handleInstituteChange} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Period"}</label>                   
                        <Select 
                            styles={styles.searchSelect}
                            label={"Diploma Period"} 
                            options={diplomaPeriods} 
                            initialValue={diploma?.privateDiplomaPeriodId!.toString()??""}
                            getSelectedValue={handleDiplomaPeriodChange} 
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Required Qualifications [SIN]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_SIN"}
                            value={diploma?.requiredQualifications_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Required Qualifications [TAM]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_TAM"}
                            value={diploma?.requiredQualifications_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Required Qualifications[ENG]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_ENG"}
                            value={diploma?.requiredQualifications_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [SIN]"}</label>
                        <QuillEditor
                            name={"privateDiplomaDetail_SIN"}
                            value={diploma?.privateDiplomaDetail_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [TAM]"}</label>
                        <QuillEditor
                            name={"privateDiplomaDetail_TAM"}
                            value={diploma?.privateDiplomaDetail_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [ENG]"}</label>
                        <QuillEditor
                            name={"privateDiplomaDetail_ENG"}
                            value={diploma?.privateDiplomaDetail_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Content [SIN]"}</label>
                        <QuillEditor
                            name={"privateDiplomaContent_SIN"}
                            value={diploma?.privateDiplomaContent_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diplom Content [TAM]"}</label>
                        <QuillEditor
                            name={"privateDiplomaContent_TAM"}
                            value={diploma?.privateDiplomaContent_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Content [ENG]"}</label>
                        <QuillEditor
                            name={"privateDiplomaContent_ENG"}
                            value={diploma?.privateDiplomaContent_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [SIN]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_SIN"}
                            value={diploma?.jobOpportunities_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [TAM]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_TAM"}
                            value={diploma?.jobOpportunities_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [ENG]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_ENG"}
                            value={diploma?.jobOpportunities_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.buttonWrapper}>
                    <Button style={styles.submitButton} onClick={postEditResponse}>{'Submit'}</Button>
                </div>
            </div>}
            <PopUpDialog styles={styles.responseDialog} open={openDialog} toggle={closeDialog}>
                <h3 style={styles.responseTitle}>{"Success"}</h3>
                <p style={styles.responseMessage}>{"The degree details are submitted successfully."}</p>                
                <Button style={styles.responseButton} type="button" onClick={closeDialog}>{"OK"}</Button>    
            </PopUpDialog>
        </>
    )
}